import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, BookBookmark, Plug, Lightbulb } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import MansionariLibrary from "@/components/MansionariLibrary";
import BillingHistory from "@/components/BillingHistory";
import IntegrationsTab from "@/components/IntegrationsTab";
import FeatureProposalForm from "@/components/FeatureProposalForm";
import CheckoutTermsDialog from "@/components/CheckoutTermsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  trial: "Trial", personal_base: "Base", personal_core: "Core", personal_top: "Top",
  business_starter: "Starter", business_core: "Core", business_top: "Top", business_enterprise: "Enterprise",
};

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "profilo";
  const [profile, setProfile] = useState({ first_name:"", last_name:"", company_name:"", role:"", password:"" });
  const [company, setCompany] = useState({ ragione_sociale:"", piva:"", sdi:"", indirizzo:"", settore:"", descrizione:"" });
  const [notifSettings, setNotifSettings] = useState({});
  const [hrReminderEmail, setHrReminderEmail] = useState("");
  const [hrEmailSaving, setHrEmailSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delEmail, setDelEmail] = useState("");
  const [delCheck, setDelCheck] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  // B71-SEXIES TASK 10: documenti accettati dell'utente (solo i propri)
  const [myAcceptances, setMyAcceptances] = useState([]);
  const [loadingAcceptances, setLoadingAcceptances] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({ first_name: user.first_name||"", last_name: user.last_name||"", company_name: user.company_name||"", role: user.role||"", password:"" });
      const s = user.settings || {};
      setCompany(s.company || {});
      setNotifSettings(s.notifications || {});
      setHrReminderEmail(s.hr_reminder_email || "");
    }
  }, [user]);

  // B71-SEXIES TASK 10: carica i documenti accettati dell'utente
  useEffect(() => {
    if (!user) return;
    setLoadingAcceptances(true);
    axios.get(`${API}/me/terms-acceptances`, { withCredentials: true })
      .then((r) => setMyAcceptances(r.data?.items || []))
      .catch(() => setMyAcceptances([]))
      .finally(() => setLoadingAcceptances(false));
  }, [user]);

  const saveProfile = async () => {
    try { await axios.put(`${API}/settings/profile`, profile, { withCredentials: true }); toast.success("Profilo aggiornato"); refreshUser(); } catch { toast.error("Errore"); }
  };
  const saveCompany = async () => {
    try { await axios.put(`${API}/settings/company`, company, { withCredentials: true }); toast.success("Dati azienda aggiornati"); refreshUser(); } catch { toast.error("Errore"); }
  };
  const saveNotifs = async () => {
    try { await axios.put(`${API}/settings`, { notifications: notifSettings }, { withCredentials: true }); toast.success("Notifiche aggiornate"); } catch { toast.error("Errore"); }
  };

  // FEATURE-04: salva email HR per reminder certificazioni
  const saveHrReminderEmail = async () => {
    const email = (hrReminderEmail || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Formato email non valido"); return;
    }
    setHrEmailSaving(true);
    try {
      await axios.put(`${API}/settings`, { hr_reminder_email: email }, { withCredentials: true });
      toast.success("Email reminder aggiornata");
      refreshUser();
    } catch { toast.error("Errore salvataggio"); }
    finally { setHrEmailSaving(false); }
  };

  const [pendingCheckout, setPendingCheckout] = useState(null);  // {planId, period} | null
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const checkout = (planId, period) => {
    // B71-QUAT TASK 4: passa per il dialog legale prima del pagamento
    setPendingCheckout({ planId, period });
  };

  const confirmCheckout = async ({ accept_terms_checkout, accept_vexatious, doc_version }) => {
    if (!pendingCheckout) return;
    setCheckoutBusy(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, {
        plan_id: pendingCheckout.planId,
        billing_period: pendingCheckout.period,
        origin_url: window.location.origin,
        accept_terms_checkout,
        accept_vexatious,
        doc_version,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel pagamento");
      setCheckoutBusy(false);
      setPendingCheckout(null);
    }
  };

  const handleDeleteMyAccount = async () => {
    if (delBusy) return;
    setDelBusy(true);
    try {
      await axios.delete(`${API}/auth/me`, { data: { email_confirm: delEmail, confirm_irreversible: delCheck }, withCredentials: true });
      toast.success("Account eliminato. Puoi recuperarlo entro 30 giorni dal login.");
      // forza logout client-side
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <PageHeader title="Impostazioni" />
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-white border border-[#0D1F3C]/8 p-1 rounded-lg mb-6">
          <TabsTrigger value="profilo" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5">Profilo</TabsTrigger>
          <TabsTrigger value="azienda" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5">Azienda</TabsTrigger>
          <TabsTrigger value="fatturazione" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5">Fatturazione</TabsTrigger>
          <TabsTrigger value="notifiche" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5">Notifiche</TabsTrigger>
          <TabsTrigger value="mansionari" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5 gap-1.5" data-testid="tab-mansionari">
            <BookBookmark size={14} weight="duotone" /> Mansionari
          </TabsTrigger>
          <TabsTrigger value="integrazioni" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5 gap-1.5" data-testid="tab-integrazioni">
            <Plug size={14} weight="duotone" /> Integrazioni
          </TabsTrigger>
          <TabsTrigger value="proposte" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-5 gap-1.5" data-testid="tab-proposte">
            <Lightbulb size={14} weight="duotone" /> Proposte
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profilo">
          <Card className="border-[#0D1F3C]/8 max-w-xl">
            <CardHeader><CardTitle className="text-base">Profilo Personale</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block">Nome</Label><Input value={profile.first_name} onChange={e=>setProfile(p=>({...p,first_name:e.target.value}))} /></div>
                <div><Label className="text-xs mb-1 block">Cognome</Label><Input value={profile.last_name} onChange={e=>setProfile(p=>({...p,last_name:e.target.value}))} /></div>
              </div>
              <div><Label className="text-xs mb-1 block">Nome Azienda</Label><Input value={profile.company_name} onChange={e=>setProfile(p=>({...p,company_name:e.target.value}))} /></div>
              <div><Label className="text-xs mb-1 block">Ruolo in Azienda</Label><Input value={profile.role} onChange={e=>setProfile(p=>({...p,role:e.target.value}))} placeholder="es. HR Manager, Recruiter" /></div>
              <div><Label className="text-xs mb-1 block">Nuova Password (lascia vuoto per non cambiare)</Label><Input type="password" value={profile.password} onChange={e=>setProfile(p=>({...p,password:e.target.value}))} placeholder="Inserisci nuova password" /></div>
              <Button onClick={saveProfile} data-testid="save-profile-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]">Salva Profilo</Button>
            </CardContent>
          </Card>
          <Card className="border-[#0D1F3C]/8 max-w-xl mt-6" data-testid="hr-reminder-email-card">
            <CardHeader><CardTitle className="text-base">Email HR per reminder certificazioni</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-[#0D1F3C]/60 leading-relaxed">
                Indirizzo destinatario delle email automatiche per le scadenze delle certificazioni dipendenti (7 giorni prima della scadenza).
                Se vuoto, vengono inviate alla tua email account.
              </p>
              <div>
                <Label className="text-xs mb-1 block">Email destinatario reminder</Label>
                <Input
                  type="email"
                  value={hrReminderEmail}
                  onChange={(e) => setHrReminderEmail(e.target.value)}
                  placeholder={user?.email || "hr@azienda.it"}
                  data-testid="hr-reminder-email-input"
                />
              </div>
              <Button onClick={saveHrReminderEmail} disabled={hrEmailSaving} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="save-hr-reminder-email-btn">
                {hrEmailSaving ? "Salvataggio…" : "Salva email reminder"}
              </Button>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/30 max-w-xl mt-6" data-testid="danger-zone-card">
            <CardHeader>
              <CardTitle className="text-sm font-bold text-red-700 uppercase tracking-wider">Zona pericolosa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-[#0D1F3C]/70">L'eliminazione e reversibile entro 30 giorni: dopo il login il tuo account verra ripristinato. Trascorso questo periodo, dati e workspace verranno cancellati definitivamente.</p>
              <Button variant="destructive" onClick={() => { setDelEmail(""); setDelCheck(false); setShowDelete(true); }} data-testid="delete-my-account-btn">
                Elimina account
              </Button>
            </CardContent>
          </Card>

          {/* B71-SEXIES TASK 10: I miei documenti accettati (read-only, user-scope) */}
          <Card className="border-[#0D1F3C]/8 max-w-xl mt-6" data-testid="my-acceptances-card">
            <CardHeader>
              <CardTitle className="text-base">I miei documenti accettati</CardTitle>
              <p className="text-xs text-[#0D1F3C]/60 mt-1">Qui trovi i documenti che hai accettato e quando.</p>
            </CardHeader>
            <CardContent>
              {loadingAcceptances ? (
                <div className="text-sm text-[#0D1F3C]/50 py-4">Caricamento…</div>
              ) : myAcceptances.length === 0 ? (
                <div className="text-sm text-[#0D1F3C]/50 py-4">Nessun documento accettato ancora.</div>
              ) : (
                <ul className="divide-y divide-[#0D1F3C]/8" data-testid="my-acceptances-list">
                  {myAcceptances.map((a, i) => {
                    const meta = {
                      condizioni: { label: "Condizioni Generali", href: "/legal/condizioni-servizio" },
                      vessatorie: { label: "Approvazione clausole vessatorie", href: "/legal/condizioni-servizio" },
                      addendum_agenzie: { label: "Addendum Partner Agenzie", href: "/legal/addendum-agenzie" },
                    }[a.doc_type] || { label: a.doc_type, href: "/legal/condizioni-servizio" };
                    const when = a.timestamp ? new Date(a.timestamp).toLocaleString("it-IT") : "—";
                    return (
                      <li key={a.id || i} className="py-3 flex items-start justify-between gap-3" data-testid={`my-acceptance-${a.doc_type}-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#0D1F3C]">{meta.label}</div>
                          <div className="text-xs text-[#0D1F3C]/60 mt-0.5">
                            <span className="font-mono">{a.doc_version}</span>
                            <span className="mx-1.5">·</span>
                            {when}
                          </div>
                        </div>
                        <a href={meta.href} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1B4FD8] underline hover:text-[#0D1F3C] whitespace-nowrap" data-testid={`my-acceptance-link-${a.doc_type}-${i}`}>
                          Rileggi
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-xs text-[#0D1F3C]/40 mt-4">
                Suggerimento: le Condizioni includono per richiamo (Art. 11) la <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1B4FD8] underline">Privacy Policy</a> e l'<a href="/legal/dpa" target="_blank" rel="noopener noreferrer" className="text-[#1B4FD8] underline">Accordo sul Trattamento dei Dati (DPA)</a>.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="azienda">
          <Card className="border-[#0D1F3C]/8 max-w-xl">
            <CardHeader><CardTitle className="text-base">Dati Azienda</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label className="text-xs mb-1 block">Ragione Sociale</Label><Input value={company.ragione_sociale||""} onChange={e=>setCompany(p=>({...p,ragione_sociale:e.target.value}))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block">P.IVA</Label><Input value={company.piva||""} onChange={e=>setCompany(p=>({...p,piva:e.target.value}))} /></div>
                <div><Label className="text-xs mb-1 block">SDI/PEC</Label><Input value={company.sdi||""} onChange={e=>setCompany(p=>({...p,sdi:e.target.value}))} /></div>
              </div>
              <div><Label className="text-xs mb-1 block">Settore Aziendale</Label>
                <Select value={company.settore||""} onValueChange={v=>setCompany(p=>({...p,settore:v}))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona settore" /></SelectTrigger>
                  <SelectContent>{["Commercio","Industria","Terziario","Artigianato","Edilizia","Trasporti","Sanita","Tecnologia","Consulenza","Ristorazione","Turismo","Agricoltura","Altro"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">Descrizione Azienda</Label><Textarea value={company.descrizione||""} onChange={e=>setCompany(p=>({...p,descrizione:e.target.value}))} rows={3} /></div>
              <Button onClick={saveCompany} data-testid="save-company-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]">Salva Dati Azienda</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fatturazione">
          <div className="space-y-6">
            <Card className="border-[#0D1F3C]/8" data-testid="current-plan-card">
              <CardHeader><CardTitle className="text-base">Piano Attivo</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge className="bg-[#1B4FD8]/10 text-[#1B4FD8] text-sm px-3 py-1" data-testid="current-plan-badge">
                    {PLAN_LABELS[user?.plan_code] || user?.plan_code || "Trial"}
                  </Badge>
                  {user?.quota && (
                    <span className="text-sm text-[#0D1F3C]/70">
                      Analisi usate questo mese: <strong>{user.quota.tests_used}</strong> / {user.quota.tests_limit >= 9999 ? "∞" : user.quota.tests_limit}
                      {user.quota.overage_count > 0 && (
                        <span className="ml-2 text-amber-700">
                          · {user.quota.overage_count} extra (€{((user.quota.overage_rate_cents || 0) / 100).toFixed(0)}/cad)
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {user?.quota?.plan_code === "trial" && typeof user?.quota?.trial_days_left === "number" && (
                  <p className="text-xs text-[#0D1F3C]/60">
                    Trial attivo — {user.quota.trial_days_left} {user.quota.trial_days_left === 1 ? "giorno" : "giorni"} rimanenti
                  </p>
                )}
                <Button
                  className="bg-[#1B4FD8] hover:bg-[#0D1F3C] mt-2"
                  onClick={() => window.location.href = "/pricing"}
                  data-testid="goto-pricing-btn"
                >
                  <CreditCard size={16} className="mr-2" />Vedi piani e cambia piano
                </Button>
              </CardContent>
            </Card>
            <BillingHistory />
          </div>
        </TabsContent>

        <TabsContent value="notifiche">
          <Card className="border-[#0D1F3C]/8 max-w-xl">
            <CardHeader><CardTitle className="text-base">Preferenze Notifiche</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {[["test_completed","Analisi completata da candidato"],["candidate_reminder","Sollecito candidato"],["employee_reminder","Sollecito dipendente"],["certification_expiry","Scadenza certificazione"],["account_expiry","Account in scadenza"]].map(([key,label])=>(
                <div key={key} className="flex items-center justify-between py-2 border-b border-[#0D1F3C]/5 last:border-0">
                  <span className="text-sm text-[#0D1F3C]/80">{label}</span>
                  <Switch checked={notifSettings[key]!==false} onCheckedChange={v=>setNotifSettings(p=>({...p,[key]:v}))} />
                </div>
              ))}
              <Button onClick={saveNotifs} data-testid="save-notifs-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]">Salva</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mansionari">
          <MansionariLibrary />
        </TabsContent>

        <TabsContent value="integrazioni">
          <IntegrationsTab />
        </TabsContent>

        <TabsContent value="proposte">
          <FeatureProposalForm />
        </TabsContent>
      </Tabs>

      {/* Dev Tools: solo admin */}
      {user?.email === "admin@selly-hr.it" && (
        <Card className="border-amber-300/40 bg-amber-50/40 max-w-xl mt-8" data-testid="dev-tools-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2">⚙️ Dev Tools (solo admin)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-[#0D1F3C]/70 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>Account type attuale:</span>
              <Badge className="bg-[#1B4FD8]/10 text-[#1B4FD8] text-xs">{user?.account_type || "personal"}</Badge>
              <span>· Max workspaces: <strong>{user?.max_workspaces_allowed ?? 1}</strong></span>
            </div>
            <div className="flex gap-2">
              {user?.account_type !== "business" && (
                <Button size="sm" onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/account-type`, { account_type: "business" }, { withCredentials: true });
                    await refreshUser();
                    toast.success("Switchato a Business");
                  } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                }} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="toggle-business">Switcha a Business</Button>
              )}
              {user?.account_type === "business" && (
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/account-type`, { account_type: "personal" }, { withCredentials: true });
                    await refreshUser();
                    toast.success("Tornato a Personal");
                  } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                }} className="border-[#0D1F3C]/15" data-testid="toggle-personal">Torna a Personal</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Dialog conferma eliminazione self-service */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent data-testid="delete-my-confirm-dialog">
          <DialogHeader><DialogTitle>Elimina il tuo account — Conferma</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#0D1F3C]/80">Stai per eliminare <strong>{user?.email}</strong>. Recuperabile per 30 giorni effettuando di nuovo il login. Dopo verra eliminato definitivamente insieme a tutti i workspace.</p>
            <div>
              <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Digita la tua email per confermare</label>
              <Input value={delEmail} onChange={(e) => setDelEmail(e.target.value)} placeholder={user?.email || ""} data-testid="delete-my-confirm-email" />
            </div>
            <div className="flex items-start gap-2">
              <Checkbox id="del-my-confirm" checked={delCheck} onCheckedChange={setDelCheck} data-testid="delete-my-confirm-check" />
              <label htmlFor="del-my-confirm" className="text-xs text-[#0D1F3C]/80 cursor-pointer">
                Confermo: hard-delete dopo 30 giorni (azione irreversibile)
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDelete(false)} disabled={delBusy}>Annulla</Button>
              <Button variant="destructive" onClick={handleDeleteMyAccount}
                disabled={delBusy || delEmail.trim().toLowerCase() !== (user?.email || "").trim().toLowerCase() || !delCheck}
                data-testid="delete-my-confirm-btn">
                {delBusy ? "Eliminazione..." : "Elimina account"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* B71-QUAT TASK 4: dialog di riconferma legale prima del checkout Stripe */}
      <CheckoutTermsDialog
        open={!!pendingCheckout}
        onClose={() => { if (!checkoutBusy) setPendingCheckout(null); }}
        onConfirm={confirmCheckout}
        busy={checkoutBusy}
      />
    </div>
  );
}
